import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useContactSession } from './ContactSessionProvider';
import {
  fetchPortalNutrition,
  connectFatSecret,
  syncPortalFatSecret,
  generateNutritionAnalysis,
} from '@/lib/api/endpoints/portal';
import { NutritionConnectedView } from '@/modules/contacts/clients/nutrition/NutritionConnectedView';
import './portal.css';

export function NutritionView() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const [searchParams] = useSearchParams();
  const { token } = useContactSession();
  const queryClient = useQueryClient();
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['portal-nutrition', workspaceId],
    queryFn: () => fetchPortalNutrition(token!, workspaceId),
    enabled: !!token && !!workspaceId,
    staleTime: 60_000,
  });

  const stats = data?.stats ?? [];
  const hasStats = stats.length > 0;
  const justConnected = searchParams.get('connected') === '1';
  const connected = (data?.connected ?? false) || justConnected;
  const analysis = data?.analysis ?? null;

  const handleConnect = async () => {
    if (!token) return;
    setConnecting(true);
    try {
      const authUrl = await connectFatSecret(token, workspaceId);
      window.location.href = authUrl;
    } catch {
      setConnecting(false);
    }
  };

  const handleSync = async () => {
    if (!token) return;
    setSyncing(true);
    setActionError(null);
    try {
      await syncPortalFatSecret(token, workspaceId);
      await queryClient.invalidateQueries({ queryKey: ['portal-nutrition', workspaceId] });
    } catch {
      setActionError('Sync failed — please try again in a moment.');
    } finally {
      setSyncing(false);
    }
  };

  const handleAnalyze = async () => {
    if (!token) return;
    setAnalyzing(true);
    setActionError(null);
    try {
      await generateNutritionAnalysis(token, workspaceId);
      await queryClient.invalidateQueries({ queryKey: ['portal-nutrition', workspaceId] });
    } catch {
      setActionError('Could not generate an analysis right now — please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  if (isLoading) {
    return <div className="portal-page"><div className="portal-spinner">Loading…</div></div>;
  }
  if (isError) {
    return <div className="portal-page"><div className="portal-error">Unable to load nutrition data. Please refresh.</div></div>;
  }

  return (
    <div className="portal-page portal-nutrition">
      <div className="portal-page-header">
        <h1>Nutrition</h1>
        {hasStats && (
          <button className="portal-btn portal-btn-sm" onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
        )}
      </div>

      {actionError && <div className="portal-error">{actionError}</div>}

      {!hasStats && (
        <div className="portal-section">
          <div className="portal-connect-cta">
            <p>{connected ? 'Connected! Your nutrition data will appear here after your first sync.' : 'Connect your nutrition tracker to see your daily intake here.'}</p>
            {connected ? (
              <button className="portal-btn" style={{ maxWidth: 260, margin: '0 auto', display: 'block' }} onClick={handleSync} disabled={syncing}>{syncing ? 'Syncing…' : 'Sync now'}</button>
            ) : (
              <button className="portal-btn" style={{ maxWidth: 260, margin: '0 auto', display: 'block' }} onClick={handleConnect} disabled={connecting}>{connecting ? 'Connecting…' : 'Connect your nutrition'}</button>
            )}
          </div>
        </div>
      )}

      {hasStats && (
        <NutritionConnectedView
          stats={stats}
          analysis={analysis}
          onAnalyze={handleAnalyze}
          analyzing={analyzing}
        />
      )}
    </div>
  );
}
